import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, Copy, Check, History, Lightbulb } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { ResultsPanel } from './ResultsPanel';
import { AIAnalysis } from './AIAnalysis';
import { InputWithTooltip } from './InputWithTooltip';
import { useCalculator, DEFAULTS, TOOLTIPS, formatCurrency, formatNumber } from '../context/CalculatorContext';
import { useAuth } from '../context/AuthContext';
import { LeadGate } from './LeadGate';
import { AdSlot } from './AdSlot';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';

export function GoogleAdsCalculator() {
  const { addToHistory, history } = useCalculator();
  const { isAdmin } = useAuth();
  
  // Form state
  const [clicsDiarios, setClicsDiarios] = useState(25);
  const [cpc, setCpc] = useState(6.23);
  const [diasMes, setDiasMes] = useState(DEFAULTS.daysPerMonth);
  const [factorAjuste, setFactorAjuste] = useState(DEFAULTS.adjustmentFactor);
  const [dontKnowMode, setDontKnowMode] = useState(false);
  const [copied, setCopied] = useState(false);

  // Calculations
  const calculations = useMemo(() => {
    const presupuestoDiarioBase = clicsDiarios * cpc;
    const presupuestoMensualOptimo = presupuestoDiarioBase * diasMes;
    // El factor de ajuste reduce el presupuesto mínimo (para clientes con menos presupuesto)
    const presupuestoMensualMinimo = presupuestoMensualOptimo * (1 - factorAjuste / 100);
    const presupuestoDiarioMinimo = presupuestoMensualMinimo / diasMes;
    const clicsEstimadosMes = clicsDiarios * diasMes;

    return {
      presupuestoDiarioBase,
      presupuestoDiarioMinimo,
      presupuestoMensualOptimo,
      presupuestoMensualMinimo,
      clicsEstimadosMes,
    };
  }, [clicsDiarios, cpc, diasMes, factorAjuste]);

  // Results for panel
  const results = useMemo(() => [
    {
      label: 'Presupuesto Diario Recomendado',
      value: calculations.presupuestoDiarioBase,
      isCurrency: true,
      isMain: true,
      suffix: 'Inversión óptima por día',
    },
    {
      label: 'Presupuesto Mensual Recomendado',
      value: calculations.presupuestoMensualOptimo,
      isCurrency: true,
      id: 'monthly-recommended',
      suffix: 'Inversión óptima mensual',
    },
    {
      label: 'Presupuesto Mensual Mínimo',
      value: calculations.presupuestoMensualMinimo,
      isCurrency: true,
      suffix: 'Con factor de ajuste aplicado',
      id: 'monthly-minimum',
    },
    {
      label: 'Clics Estimados al Mes',
      value: calculations.clicsEstimadosMes,
      isCurrency: false,
      id: 'estimated-clicks',
    },
    {
      label: 'CPC Utilizado',
      value: cpc,
      isCurrency: true,
      id: 'cpc-used',
    },
  ], [calculations, cpc]);

  // AI context
  const aiContext = useMemo(() => 
    `El cliente quiere obtener ${clicsDiarios} clics diarios en Google Ads. El CPC de referencia de su industria es $${cpc} MXN. El presupuesto diario recomendado es $${calculations.presupuestoDiarioBase.toFixed(2)} MXN y el mensual óptimo es $${calculations.presupuestoMensualOptimo.toFixed(2)} MXN. El presupuesto mínimo mensual es $${calculations.presupuestoMensualMinimo.toFixed(2)} MXN. Analiza si este presupuesto es coherente con el objetivo y la industria, e indica si deberían aumentar el CPC objetivo o el volumen de clics.`,
    [clicsDiarios, cpc, calculations]
  );

  // Save to history
  const saveToHistory = useCallback(() => {
    addToHistory({
      module: 'Google Ads',
      data: {
        clicsDiarios,
        cpc,
        presupuestoMensual: calculations.presupuestoMensualOptimo,
      },
    });
  }, [addToHistory, clicsDiarios, cpc, calculations]);

  // Generate summary text
  const summaryText = useMemo(() => `📊 Propuesta de inversión publicitaria
Plataforma: Google Ads
Objetivo: ${clicsDiarios} clics diarios

Presupuesto diario recomendado: ${formatCurrency(calculations.presupuestoDiarioBase)}
Presupuesto mensual recomendado: ${formatCurrency(calculations.presupuestoMensualOptimo)}
Presupuesto mensual mínimo: ${formatCurrency(calculations.presupuestoMensualMinimo)}
Clics estimados al mes: ${formatNumber(calculations.clicsEstimadosMes)}
CPC utilizado: ${formatCurrency(cpc)}

*Esta estimación está basada en benchmarks de la industria y puede variar según la optimización de las campañas.*`, 
    [clicsDiarios, cpc, calculations]);

  const handleCopySummary = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      saveToHistory();
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for environments where clipboard API is not available
      const textArea = document.createElement('textarea');
      textArea.value = summaryText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        saveToHistory();
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Copy failed:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  // Don't know mode suggestions
  const suggestedCPC = 6.23; // Average across industries

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25">
              <Search className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Google Ads</h1>
              <p className="text-slate-500">CPC · Búsqueda y Display</p>
            </div>
          </div>
        </motion.div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Input Column */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-7 space-y-6"
          >
            {/* Don't Know Mode */}
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  <div>
                    <p className="font-semibold text-amber-800">¿No sabes el CPC?</p>
                    <p className="text-sm text-amber-700">Usaremos un promedio de ${suggestedCPC} MXN</p>
                  </div>
                </div>
                <Switch
                  checked={dontKnowMode}
                  onCheckedChange={(checked) => {
                    setDontKnowMode(checked);
                    if (checked) setCpc(suggestedCPC);
                  }}
                  data-testid="dont-know-mode-switch"
                />
              </div>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
              <InputWithTooltip label="Meta de Clics diario" tooltip={TOOLTIPS.clicsDiarios}>
                <Input
                  type="number"
                  value={clicsDiarios}
                  onChange={(e) => setClicsDiarios(Number(e.target.value) || 0)}
                  placeholder="Ej: 25"
                  className="h-12 text-lg"
                  data-testid="google-clics-input"
                />
              </InputWithTooltip>

              <InputWithTooltip label="CPC (Costo por Clic)" tooltip={TOOLTIPS.cpc}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={cpc}
                    onChange={(e) => setCpc(Number(e.target.value) || 0)}
                    placeholder="Ej: 6.23"
                    className="h-12 text-lg pl-8 font-mono"
                    disabled={dontKnowMode}
                    data-testid="google-cpc-input"
                  />
                </div>
              </InputWithTooltip>

              <InputWithTooltip label="Días al mes" tooltip="Número de días del mes a considerar">
                <Input
                  type="number"
                  value={diasMes}
                  onChange={(e) => setDiasMes(Number(e.target.value) || 30)}
                  placeholder="30"
                  className="h-12 text-lg"
                  data-testid="google-dias-input"
                />
              </InputWithTooltip>

              <InputWithTooltip label="Factor de ajuste (%)" tooltip={TOOLTIPS.adjustmentFactor}>
                <div className="relative">
                  <Input
                    type="number"
                    value={factorAjuste}
                    onChange={(e) => setFactorAjuste(Number(e.target.value) || 0)}
                    placeholder="15"
                    className="h-12 text-lg pr-8"
                    data-testid="google-ajuste-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">%</span>
                </div>
              </InputWithTooltip>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex-1 gap-2 h-12"
                    data-testid="generate-summary-button"
                  >
                    <Copy className="h-4 w-4" />
                    Generar Resumen
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Resumen para copiar</DialogTitle>
                  </DialogHeader>
                  <div className="mt-4">
                    <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg border border-slate-200 font-mono">
                      {summaryText}
                    </pre>
                    <Button 
                      onClick={handleCopySummary} 
                      className="w-full mt-4 gap-2"
                      data-testid="copy-summary-button"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? 'Copiado' : 'Copiar al portapapeles'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {history.length > 0 && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" className="gap-2 h-12" data-testid="history-button">
                      <History className="h-4 w-4" />
                      Historial ({history.length})
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Historial de cálculos</DialogTitle>
                    </DialogHeader>
                    <div className="mt-4 space-y-3">
                      {history.filter(h => h.module === 'Google Ads').map((item, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <p className="font-semibold text-slate-900">{item.module}</p>
                          <p className="text-sm text-slate-600">
                            {item.data.clicsDiarios} clics/día · CPC ${item.data.cpc}
                          </p>
                          <p className="text-sm font-mono text-[#1A56DB]">
                            {formatCurrency(item.data.presupuestoMensual)}/mes
                          </p>
                        </div>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {/* AI Analysis */}
            <AIAnalysis 
              module="google" 
              contextData={aiContext}
              disabled={!clicsDiarios || !cpc}
            />

            {/* Disclaimer */}
            <p className="text-xs text-slate-500 text-center">
              Los resultados son estimaciones basadas en benchmarks de la industria. Los resultados reales dependen de la calidad creativa, la segmentación y el período de optimización del algoritmo.
            </p>
          </motion.div>

          {/* Results Column */}
          <div className="lg:col-span-5">
            <LeadGate>
              <ResultsPanel 
                title="Resultados Google Ads"
                results={results}
                showMinInvestmentWarning={calculations.presupuestoMensualMinimo < DEFAULTS.minInvestmentWarning}
              />
            </LeadGate>
            {!isAdmin && (
              <AdSlot slotId="2222222222" variant="rectangle" testId="google-ad-rectangle" className="mt-8" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
